import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import IconButton from '@mui/material/IconButton';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import CodeIcon from '@mui/icons-material/Code';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LinkIcon from '@mui/icons-material/Link';
import TitleIcon from '@mui/icons-material/Title';
import TooltipBase, { tooltipClasses } from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

// TipTap imports
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import CharacterCount from '@tiptap/extension-character-count';

const CustomTooltip = styled(({ className, ...props }: any) => (
  <TooltipBase {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '0.875rem',
  },
});

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.mode === 'dark' ? '#1a1a1a' : '#ffffff'};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
  border-bottom: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  margin: 0 8px;
`;

const ToolButton = styled(IconButton)<{ $active?: boolean }>`
  && {
    padding: 6px;
    border-radius: 6px;
    color: ${({ theme, $active }) => $active
      ? (theme.mode === 'dark' ? '#60a5fa' : '#2563eb')
      : (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)')};
    background: ${({ $active }) => $active ? 'rgba(37, 99, 235, 0.1)' : 'transparent'};
    &:hover {
      background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
    }
    &:disabled {
      color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
    }
  }
`;

const HeadingButton = styled(ToolButton)`
  && {
    font-size: 0.85rem;
    font-weight: 600;
    min-width: 28px;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  overflow: auto;
  padding: 24px 32px;
  background: ${({ theme }) => theme.mode === 'dark' ? '#1a1a1a' : '#ffffff'};
`;

const StyledEditorContent = styled(EditorContent)`
  min-height: 100%;
  max-width: 800px;
  margin: 0 auto;

  .tiptap {
    min-height: 100%;
    padding: 20px;
    font-family: 'Georgia', 'Times New Roman', serif;
    font-size: 1rem;
    line-height: 1.8;
    color: ${({ theme }) => theme.font_color};
    background: ${({ theme }) => theme.mode === 'dark' ? '#1e1e1e' : '#fafafa'};
    border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
    border-radius: 8px;
    box-shadow: ${({ theme }) => theme.mode === 'dark'
      ? '0 4px 12px rgba(0, 0, 0, 0.4)'
      : '0 4px 12px rgba(0, 0, 0, 0.08)'};
    outline: none;

    &:focus {
      border-color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(96, 165, 250, 0.5)' : 'rgba(37, 99, 235, 0.3)'};
    }

    h1, h2, h3, h4, h5, h6 {
      font-family: 'Arial', sans-serif;
      margin: 1em 0 0.5em 0;
      color: ${({ theme }) => theme.font_color};
    }

    h1 { font-size: 2em; font-weight: 700; }
    h2 { font-size: 1.5em; font-weight: 600; }
    h3 { font-size: 1.25em; font-weight: 600; }

    p {
      margin: 0.5em 0;
    }

    ul, ol {
      margin: 0.5em 0;
      padding-left: 1.5em;
    }

    li {
      margin: 0.25em 0;
    }

    blockquote {
      margin: 1em 0;
      padding: 0.5em 1em;
      border-left: 4px solid ${({ theme }) => theme.mode === 'dark' ? '#60a5fa' : '#2563eb'};
      background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(96, 165, 250, 0.1)' : 'rgba(37, 99, 235, 0.05)'};
      font-style: italic;
    }

    pre, code {
      font-family: 'Monaco', 'Menlo', monospace;
      background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
      border-radius: 4px;
    }

    code {
      padding: 0.1em 0.3em;
      font-size: 0.9em;
    }

    pre {
      padding: 1em;
      overflow-x: auto;
    }

    a {
      color: ${({ theme }) => theme.mode === 'dark' ? '#60a5fa' : '#2563eb'};
      text-decoration: underline;
    }

    hr {
      border: none;
      border-top: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
      margin: 1.5em 0;
    }
  }
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'};
  border-top: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
`;

const LinkDialog = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 20px;
  background: ${({ theme }) => theme.mode === 'dark' ? '#2a2a2a' : '#ffffff'};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  min-width: 400px;
`;

const LinkDialogTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 1rem;
  color: ${({ theme }) => theme.font_color};
`;

const LinkInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.font_color};
  background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 6px;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.mode === 'dark' ? '#60a5fa' : '#2563eb'};
  }
`;

const LinkDialogButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const DialogButton = styled.button<{ $primary?: boolean }>`
  padding: 8px 16px;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  border: none;
  background: ${({ theme, $primary }) => $primary
    ? (theme.mode === 'dark' ? '#60a5fa' : '#2563eb')
    : (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)')};
  color: ${({ theme, $primary }) => $primary
    ? '#ffffff'
    : theme.font_color};
  &:hover {
    opacity: 0.9;
  }
`;

const LinkDialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

interface RichTextEditorProps {
  initialContent: string;
  onChange?: (content: string) => void;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent,
  onChange,
}) => {
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [headingMenuAnchor, setHeadingMenuAnchor] = useState<HTMLElement | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      CharacterCount,
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  // Update content when initialContent changes
  useEffect(() => {
    if (editor && initialContent !== editor.getHTML()) {
      editor.commands.setContent(initialContent);
    }
  }, [initialContent, editor]);

  const handleBold = useCallback(() => {
    editor?.chain().focus().toggleBold().run();
  }, [editor]);

  const handleItalic = useCallback(() => {
    editor?.chain().focus().toggleItalic().run();
  }, [editor]);

  const handleUnderline = useCallback(() => {
    editor?.chain().focus().toggleUnderline().run();
  }, [editor]);

  const handleStrikethrough = useCallback(() => {
    editor?.chain().focus().toggleStrike().run();
  }, [editor]);

  const handleOrderedList = useCallback(() => {
    editor?.chain().focus().toggleOrderedList().run();
  }, [editor]);

  const handleUnorderedList = useCallback(() => {
    editor?.chain().focus().toggleBulletList().run();
  }, [editor]);

  const handleAlignLeft = useCallback(() => {
    editor?.chain().focus().setTextAlign('left').run();
  }, [editor]);

  const handleAlignCenter = useCallback(() => {
    editor?.chain().focus().setTextAlign('center').run();
  }, [editor]);

  const handleAlignRight = useCallback(() => {
    editor?.chain().focus().setTextAlign('right').run();
  }, [editor]);

  const handleAlignJustify = useCallback(() => {
    editor?.chain().focus().setTextAlign('justify').run();
  }, [editor]);

  const handleUndo = useCallback(() => {
    editor?.chain().focus().undo().run();
  }, [editor]);

  const handleRedo = useCallback(() => {
    editor?.chain().focus().redo().run();
  }, [editor]);

  const handleClearFormat = useCallback(() => {
    editor?.chain().focus().unsetAllMarks().clearNodes().run();
  }, [editor]);

  const handleHeading = useCallback((level: number) => {
    if (level === 0) {
      editor?.chain().focus().setParagraph().run();
    } else {
      editor?.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run();
    }
    setHeadingMenuAnchor(null);
  }, [editor]);

  const handleCode = useCallback(() => {
    editor?.chain().focus().toggleCode().run();
  }, [editor]);

  const handleQuote = useCallback(() => {
    editor?.chain().focus().toggleBlockquote().run();
  }, [editor]);

  const handleLinkClick = useCallback(() => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);

    setLinkText(selectedText);
    setLinkUrl(editor.getAttributes('link').href || '');
    setShowLinkDialog(true);
  }, [editor]);

  const handleInsertLink = useCallback(() => {
    if (!editor) return;

    if (linkUrl) {
      const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;

      if (linkText && editor.state.selection.empty) {
        // Insert new text with link
        editor
          .chain()
          .focus()
          .insertContent(`<a href="${url}">${linkText}</a>`)
          .run();
      } else {
        // Apply link to selected text
        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: url })
          .run();
      }
    } else {
      // Remove link
      editor.chain().focus().unsetLink().run();
    }

    setShowLinkDialog(false);
    setLinkUrl('');
    setLinkText('');
  }, [editor, linkUrl, linkText]);

  if (!editor) {
    return null;
  }

  const wordCount = editor.storage.characterCount.words();
  const charCount = editor.storage.characterCount.characters();

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarGroup>
          <CustomTooltip title="Desfazer (Ctrl+Z)" placement="bottom">
            <ToolButton
              onClick={handleUndo}
              disabled={!editor.can().undo()}
            >
              <UndoIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
          <CustomTooltip title="Refazer (Ctrl+Y)" placement="bottom">
            <ToolButton
              onClick={handleRedo}
              disabled={!editor.can().redo()}
            >
              <RedoIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <CustomTooltip title="Titulo" placement="bottom">
            <HeadingButton onClick={(e) => setHeadingMenuAnchor(e.currentTarget)}>
              <TitleIcon fontSize="small" />
            </HeadingButton>
          </CustomTooltip>
          <Menu
            anchorEl={headingMenuAnchor}
            open={Boolean(headingMenuAnchor)}
            onClose={() => setHeadingMenuAnchor(null)}
          >
            <MenuItem onClick={() => handleHeading(0)}>Parágrafo</MenuItem>
            <MenuItem onClick={() => handleHeading(1)}>Título 1</MenuItem>
            <MenuItem onClick={() => handleHeading(2)}>Título 2</MenuItem>
            <MenuItem onClick={() => handleHeading(3)}>Título 3</MenuItem>
          </Menu>
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <CustomTooltip title="Negrito (Ctrl+B)" placement="bottom">
            <ToolButton $active={editor.isActive('bold')} onClick={handleBold}>
              <FormatBoldIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
          <CustomTooltip title="Itálico (Ctrl+I)" placement="bottom">
            <ToolButton $active={editor.isActive('italic')} onClick={handleItalic}>
              <FormatItalicIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
          <CustomTooltip title="Sublinhado (Ctrl+U)" placement="bottom">
            <ToolButton $active={editor.isActive('underline')} onClick={handleUnderline}>
              <FormatUnderlinedIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
          <CustomTooltip title="Tachado" placement="bottom">
            <ToolButton $active={editor.isActive('strike')} onClick={handleStrikethrough}>
              <StrikethroughSIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <CustomTooltip title="Lista com marcadores" placement="bottom">
            <ToolButton $active={editor.isActive('bulletList')} onClick={handleUnorderedList}>
              <FormatListBulletedIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
          <CustomTooltip title="Lista numerada" placement="bottom">
            <ToolButton $active={editor.isActive('orderedList')} onClick={handleOrderedList}>
              <FormatListNumberedIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <CustomTooltip title="Alinhar à esquerda" placement="bottom">
            <ToolButton $active={editor.isActive({ textAlign: 'left' })} onClick={handleAlignLeft}>
              <FormatAlignLeftIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
          <CustomTooltip title="Centralizar" placement="bottom">
            <ToolButton $active={editor.isActive({ textAlign: 'center' })} onClick={handleAlignCenter}>
              <FormatAlignCenterIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
          <CustomTooltip title="Alinhar à direita" placement="bottom">
            <ToolButton $active={editor.isActive({ textAlign: 'right' })} onClick={handleAlignRight}>
              <FormatAlignRightIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
          <CustomTooltip title="Justificar" placement="bottom">
            <ToolButton $active={editor.isActive({ textAlign: 'justify' })} onClick={handleAlignJustify}>
              <FormatAlignJustifyIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <CustomTooltip title="Código" placement="bottom">
            <ToolButton $active={editor.isActive('code')} onClick={handleCode}>
              <CodeIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
          <CustomTooltip title="Citação" placement="bottom">
            <ToolButton $active={editor.isActive('blockquote')} onClick={handleQuote}>
              <FormatQuoteIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
          <CustomTooltip title="Inserir link" placement="bottom">
            <ToolButton $active={editor.isActive('link')} onClick={handleLinkClick}>
              <LinkIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
        </ToolbarGroup>

        <ToolbarDivider />

        <ToolbarGroup>
          <CustomTooltip title="Limpar formatação" placement="bottom">
            <ToolButton onClick={handleClearFormat}>
              <FormatClearIcon fontSize="small" />
            </ToolButton>
          </CustomTooltip>
        </ToolbarGroup>
      </Toolbar>

      <ContentArea>
        <StyledEditorContent editor={editor} />
      </ContentArea>

      <StatusBar>
        <span>{wordCount} palavras, {charCount} caracteres</span>
      </StatusBar>

      {showLinkDialog && (
        <>
          <LinkDialogOverlay onClick={() => setShowLinkDialog(false)} />
          <LinkDialog>
            <LinkDialogTitle>Inserir Link</LinkDialogTitle>
            <LinkInput
              placeholder="Texto do link"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
            />
            <LinkInput
              placeholder="URL (ex: https://example.com)"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              autoFocus
            />
            <LinkDialogButtons>
              <DialogButton onClick={() => setShowLinkDialog(false)}>
                Cancelar
              </DialogButton>
              <DialogButton $primary onClick={handleInsertLink}>
                Inserir
              </DialogButton>
            </LinkDialogButtons>
          </LinkDialog>
        </>
      )}
    </EditorContainer>
  );
};

export default RichTextEditor;
